/* eslint-disable jsx-a11y/anchor-is-valid */
/* eslint-disable @typescript-eslint/default-param-last */
import { useMemo } from 'react';
import Grid from '@elliemae/ds-grid';
import {
  getMIKey,
  isMIFirstPhaseLoading,
  isMILoading,
  isMIError,
  MI_ALREADY_ORDERED_TOOLTIP,
  MI_NOT_APPLICABLE_TOOLTIP,
  MILoadingSkeleton,
  useMIContext,
  useMIRowState,
  GetMIButton,
} from '../GetMIButton';
import Rights from '../../../../common/services/Rights';
import { CustomHeader } from '../../../components/CustomColumnHeader/index';
import {
  dispApr,
  dispCurrencyMask,
  dispDti,
  dispPrice,
  NON_CONVENTIONAL_PRODUCT_TYPES,
  shouldDisplayAPR,
  shouldShowGetMIColumn,
  toNumber,
} from '../../helpers';
import {
  StyledBestExLockLink,
  StyledBestExProgramLinks,
  StyledBestExRowSelected,
} from './styled';

interface GetBestExColumnsProps {
  relockRequest?: any;
  selectedRowID?: string;
  handleSelectProduct?: any;
  handleRefreshButton?: any;
  handleSelectRow?: any;
  disableLockLinks?: boolean;
  windowWidth?: number;
  isBuyside?: boolean;
  isBuySideHistorical?: boolean;
  requestLockStatus?: number;
  loanData?: any;
  clientSettings?: any;
  originalLoanInformation?: any;
  loanTermsLookup?: any[];
  miCertification?: any;
  proposedMIAmount?: number;
}

const rightAlign = {
  width: '100%',
  textAlign: 'right',
  paddingRight: '3px',
  border: '1px none white',
};

const CellValue = ({ value, isBest, styles }) => {
  if (isBest) {
    return <span style={{ fontWeight: 'bold', ...styles }}>{value}</span>;
  }
  return <span style={{ ...styles }}>{value}</span>;
};

const getSelectedLock = (lockRequested, locksAvailable) => {
  const locksArr = locksAvailable.split(',').map(Number);
  const locks = locksArr.filter((lock) => lock >= lockRequested);
  return locks[0];
};

export const getBestExColumns = ({
  relockRequest = {},
  selectedRowID = '',
  handleSelectProduct,
  handleRefreshButton,
  handleSelectRow,
  disableLockLinks,
  windowWidth,
  requestLockStatus,
  clientSettings,
  loanData,
  originalLoanInformation,
  loanTermsLookup,
  miCertification,
  proposedMIAmount,
}: GetBestExColumnsProps) => {
  const standardProducts = loanData?.standardProducts || [];
  const showGetMIColumn = shouldShowGetMIColumn({
    clientSettings,
    ltv: loanData?.loanInformation?.ltv
      ? parseFloat(loanData?.loanInformation?.ltv)
      : originalLoanInformation?.ltv,
    standardProducts,
    loanTerms: loanData?.productType || [],
    loanTermsLookup,
    loanChannel: loanData?.loanInformation?.loanChannel,
  });
  const numCols =
    (shouldDisplayAPR(clientSettings) ? 7 : 6) + (showGetMIColumn ? 2 : 0);
  const columnWidth = `${(48 / numCols).toFixed(2)}%`;
  const displayEstimatedMI = clientSettings?.[0]?.options?.displayEstimatedMI;

  return [
    {
      accessor: 'program',
      Header: 'Program Name',
      minWidth: '60px',
      width: '52%',
      Cell: ({ row }) => {
        const rowData = row.original;
        const isSelectedRow = rowData.id === selectedRowID;
        const { productTypeID } = rowData;
        const selectedLock = getSelectedLock(
          rowData.lockRequested,
          rowData.locksAvailable,
        );
        let productIcon;
        let altText;
        if (productTypeID === 2) {
          // TODO(ngen): moving all these files to just URL references from webroot
          productIcon = '/img/fha.gif';
          altText = 'FHA';
        } else if (productTypeID === 3) {
          productIcon = '/img/va.gif';
          altText = 'VA';
        } else if (productTypeID === 4) {
          productIcon = '/img/usda.gif';
          altText = 'USDA';
        }
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            <CellValue
              isBest={rowData.isBest}
              styles={rightAlign}
              value={
                <Grid
                  cols={[1, 1, 1]}
                  style={{ alignItems: 'left', border: '1px none red' }}
                >
                  <Grid style={{ width: '250px' }}>
                    <span
                      style={{
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        border: '1px none yellow',
                      }}
                    >
                      {rowData.productGroup}
                    </span>
                  </Grid>
                  <Grid
                    style={{
                      width: '70%',
                      paddingRight: '20px',
                      border: '1px none blue',
                      alignItems: 'center',
                      display: 'block',
                    }}
                  >
                    <span>
                      ({' '}
                      {rowData.locksAvailable
                        .split(',')
                        .map((lock) =>
                          selectedLock !== Number(lock) ? (
                            <StyledBestExLockLink
                              key={lock}
                              disabled={disableLockLinks}
                              aria-label={`${lock} day lock`}
                              onClick={() => handleRefreshButton(lock)}
                            >
                              {lock}
                            </StyledBestExLockLink>
                          ) : (
                            <span style={{ fontWeight: 'bold' }} key={lock}>
                              {lock}
                            </span>
                          ),
                        )
                        .reduce((prev, curr) => [prev, ' ', curr])}{' '}
                      )
                    </span>
                  </Grid>
                  <Grid>
                    {productIcon && (
                      <img
                        src={productIcon}
                        alt={altText}
                        style={{ height: '10px' }}
                      />
                    )}
                  </Grid>
                </Grid>
              }
            />
          </StyledBestExRowSelected>
        );
      },
    },
    {
      accessor: 'rate',
      Header: () => <CustomHeader label="Rate" />,
      width: columnWidth,
      sortable: true,
      resizable: true,
      Cell: ({ row }) => {
        const rowData = row.original.target;
        const { programID, rateDataID } = rowData;

        const lockEffectiveDate =
          row.original.rateDetails.find(
            (rateDetail) => rateDetail.rateDataID === rateDataID,
          )?.lockEffectiveDate || null;
        const wcpPolicyApplied = row.original.wcpPolicyApplied;
        const isSelectedRow = rowData.id === selectedRowID;
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            <Grid width="100%" justifyContent="flex-end" alignItems="center">
              <StyledBestExProgramLinks
                onClick={() => {
                  handleSelectRow(rowData);
                  handleSelectProduct(
                    programID,
                    rateDataID,
                    lockEffectiveDate,
                    wcpPolicyApplied,
                  );
                }}
              >
                {rowData.rate ? `${rowData.rate.toFixed(3)}` : null}
              </StyledBestExProgramLinks>
            </Grid>
          </StyledBestExRowSelected>
        );
      },
    },
    {
      accessor: 'price',
      Header: () => <CustomHeader label="PRICE" />,
      width: columnWidth,
      searchable: true,
      resizable: true,
      Cell: ({ row }) => {
        const rowData = row.original.target;
        const isSelectedRow = rowData.id === selectedRowID;
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            <CellValue
              styles={rightAlign}
              value={dispPrice(rowData.price, rowData.priceVal)}
              isBest={rowData.isBest}
            />
          </StyledBestExRowSelected>
        );
      },
    },
    {
      accessor: 'discount',
      Header: () => <CustomHeader label="Price ($)" />,
      width: columnWidth,
      Cell: ({ row }) => {
        const rowData = row.original.target;
        const isSelectedRow = rowData.id === selectedRowID;
        if (!rowData.parent) {
          return (
            <StyledBestExRowSelected data-isSelected={isSelectedRow}>
              <CellValue
                styles={rightAlign}
                value={dispCurrencyMask(rowData.priceVal)}
                isBest={rowData.isBest}
              />
            </StyledBestExRowSelected>
          );
        }
        return '';
      },
    },
    {
      accessor: 'pnI',
      Header: () => <CustomHeader label="PMT" />,
      width: columnWidth,
      resizable: true,
      Cell: ({ row }) => {
        const rowData = row.original.target;
        const isSelectedRow = rowData.id === selectedRowID;
        const isMIEligible = useMemo(
          () =>
            !NON_CONVENTIONAL_PRODUCT_TYPES.includes(rowData?.productTypeID),
          [rowData?.productTypeID],
        );
        const { status, miAmount: storedMIAmount } = useMIRowState(
          getMIKey(rowData?.rateDataID, rowData?.programID),
        );
        const miAmount = useMemo(
          () =>
            isMIEligible && miCertification ? proposedMIAmount : storedMIAmount,
          [isMIEligible, storedMIAmount],
        );
        const isLoading = useMemo(
          () => isMIFirstPhaseLoading(status) && !miCertification,
          [status],
        );
        const displayPnI = toNumber(rowData.pnI) + (miAmount ?? 0);
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            {isLoading ? (
              <MILoadingSkeleton />
            ) : (
              <CellValue
                styles={rightAlign}
                value={`$${Math.round(displayPnI)}`}
                isBest={rowData.isBest}
              />
            )}
          </StyledBestExRowSelected>
        );
      },
    },
    ...(showGetMIColumn
      ? [
          {
            accessor: 'mi',
            Header: () => <CustomHeader label="MI PMT" />,
            width: columnWidth,
            sortable: false,
            Cell: ({ row }) => {
              const rowData = row.original;
              const targetData = row.original.target;
              const isSelectedRow = rowData.id === selectedRowID;
              const isMIEligible = useMemo(
                () =>
                  !NON_CONVENTIONAL_PRODUCT_TYPES.includes(
                    rowData?.productTypeID,
                  ),
                [rowData?.productTypeID],
              );
              const { status, miAmount: storedMIAmount } = useMIRowState(
                getMIKey(targetData?.rateDataID, targetData?.programID),
              );
              const miAmount = useMemo(
                () =>
                  isMIEligible && miCertification
                    ? proposedMIAmount
                    : storedMIAmount,
                [isMIEligible, storedMIAmount],
              );
              const isLoading = useMemo(
                () => isMIFirstPhaseLoading(status) && !miCertification,
                [status],
              );
              return (
                <StyledBestExRowSelected
                  data-isSelected={isSelectedRow}
                  showMIColumn
                >
                  <Grid
                    width="100%"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    {isLoading ? (
                      <MILoadingSkeleton />
                    ) : miAmount != null ? (
                      <span style={rightAlign}>{`$${Math.round(
                        miAmount,
                      )}`}</span>
                    ) : null}
                  </Grid>
                </StyledBestExRowSelected>
              );
            },
          },
          {
            accessor: 'getMI',
            Header: () => <CustomHeader label="GET MI" />,
            width: columnWidth,
            sortable: false,
            Cell: ({ row }) => {
              const { handleGetMI } = useMIContext();
              const rowData = row.original;
              const targetData = row.original.target;
              const isSelectedRow = rowData.id === selectedRowID;
              const isMIEligible = useMemo(
                () =>
                  !NON_CONVENTIONAL_PRODUCT_TYPES.includes(
                    rowData.productTypeID,
                  ),
                [rowData.productTypeID],
              );
              const isDisabled = useMemo(
                () => !isMIEligible || !!miCertification,
                [isMIEligible],
              );
              const disabledTooltip = useMemo(
                () =>
                  miCertification && isMIEligible
                    ? MI_ALREADY_ORDERED_TOOLTIP
                    : MI_NOT_APPLICABLE_TOOLTIP,
                [isMIEligible],
              );
              const { status, miError } = useMIRowState(
                getMIKey(targetData?.rateDataID, targetData?.programID),
              );
              const isLoading = useMemo(
                () => isMIFirstPhaseLoading(status) && !miCertification,
                [status],
              );
              return (
                <StyledBestExRowSelected
                  data-isSelected={isSelectedRow}
                  showMIColumn
                >
                  <Grid
                    width="100%"
                    justifyContent="flex-end"
                    alignItems="flex-end"
                  >
                    {isLoading ? (
                      <MILoadingSkeleton />
                    ) : (
                      <GetMIButton
                        isDisabled={isDisabled}
                        disabledTooltip={disabledTooltip}
                        isLoading={false}
                        isError={isMIError(status)}
                        errorTooltip={miError?.tooltipMessage}
                        onClick={() =>
                          handleGetMI?.({
                            rateDataID: targetData?.rateDataID,
                            programID: targetData?.programID,
                            rateData: targetData,
                            displayEstimatedMI,
                          })
                        }
                      />
                    )}
                  </Grid>
                </StyledBestExRowSelected>
              );
            },
          },
        ]
      : []),
    ...(shouldDisplayAPR(clientSettings)
      ? [
          {
            accessor: 'apr',
            Header: () => <CustomHeader label="APR" />,
            width: columnWidth,
            Cell: ({ row }) => {
              const rowData = row.original.target;
              const isSelectedRow = rowData.id === selectedRowID;
              const { status, aprValue } = useMIRowState(
                getMIKey(rowData?.rateDataID, rowData?.programID),
              );
              const isLoading = isMILoading(status);
              const displayApr = aprValue != null ? aprValue : rowData.apr;
              if (!rowData.parent) {
                return (
                  <StyledBestExRowSelected data-isSelected={isSelectedRow}>
                    {isLoading ? (
                      <MILoadingSkeleton />
                    ) : (
                      <CellValue
                        styles={rightAlign}
                        value={dispApr(displayApr)}
                        isBest={rowData.isBest}
                      />
                    )}
                  </StyledBestExRowSelected>
                );
              }
              return '';
            },
          },
        ]
      : []),
    {
      accessor: 'bRatio',
      Header: () => <CustomHeader label="DTI" />,
      width: columnWidth,
      Cell: ({ row }) => {
        const rowData = row.original.target;
        const isSelectedRow = rowData.id === selectedRowID;
        if (!rowData.parent) {
          return (
            <StyledBestExRowSelected data-isSelected={isSelectedRow}>
              <CellValue
                styles={rightAlign}
                value={dispDti(rowData.bRatio)}
                isBest={rowData.isBest}
              />
            </StyledBestExRowSelected>
          );
        }
        return '';
      },
    },
    {
      accessor: 'details',
      Header: () => <CustomHeader label="QM/DET" />,
      width: columnWidth,
      searchable: true,
      resizable: true,
      Cell: ({ row, onClickPrice }) => {
        const rowData = row.original.target;
        const isSelectedRow = rowData.id === selectedRowID;
        const { programID, rateDataID } = rowData;
        const lockEffectiveDate =
          row.original.rateDetails.find(
            (rateDetail) => rateDetail.rateDataID === rateDataID,
          )?.lockEffectiveDate || null;
        const wcpPolicyApplied = row.original.wcpPolicyApplied;
        return (
          <StyledBestExRowSelected data-isSelected={isSelectedRow}>
            <Grid width="100%" justifyContent="flex-end" alignItems="center">
              <StyledBestExProgramLinks
                aria-label={
                  rowData.rate
                    ? `Details for rate ${rowData.rate.toFixed(3)}`
                    : 'Details'
                }
                onClick={() => {
                  onClickPrice(rowData);
                }}
              >
                details
              </StyledBestExProgramLinks>
            </Grid>
          </StyledBestExRowSelected>
        );
      },
    },
  ];
};
